import axios from 'axios';
import React, { useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { useMsal } from '@azure/msal-react';
import { useTranslation } from 'react-i18next';
import { config } from '../../Config/AppConfig';
import { saveLoginExpiredState } from '../../utils/sessionStorage';

/**
 * React context to allow access to a single axios instance for use by the application.
 */
const AxiosContext = React.createContext(null);

/**
 * Provides two instances of axios for use by the application - a public client (no authentication),
 * and an authenticated version, which attempts to set the bearer token in each request.
 *
 * @returns the provider component.
 */
function AxiosProvider({ children }) {
  const { i18n } = useTranslation();
  const { instance } = useMsal();

  const publicAxiosInstance = axios.create();
  publicAxiosInstance.interceptors.request.use(
    async (requestConfig) => {
      // Provide the language header so the API knows what language to format potential messages in.
      // eslint-disable-next-line no-param-reassign
      requestConfig.headers[`Accept-Language`] = i18n.language;
      return requestConfig;
    },
    (error) => error
  );

  const authenticatedAxiosInstance = axios.create();
  authenticatedAxiosInstance.interceptors.request.use(
    async (requestConfig) => {
      const abortController = new AbortController();

      let accessToken = '';
      const activeAccount = instance.getActiveAccount();
      if (activeAccount) {
        const silentRequest = {
          account: activeAccount,
          scopes: [`${config.msal.TOKEN_DEFAULT_SCOPE}`],
        };
        await instance
          .acquireTokenSilent(silentRequest)
          .then((response) => {
            accessToken = response.accessToken;
            saveLoginExpiredState(false);
          })
          .catch(async () => {
            saveLoginExpiredState(true);
          });
      } else {
        saveLoginExpiredState(false);
      }

      if (accessToken) {
        // eslint-disable-next-line no-param-reassign
        requestConfig.headers.Authorization = `Bearer ${accessToken}`;
        // Provide the language header so the API knows what language to format potential messages in.
        // eslint-disable-next-line no-param-reassign
        requestConfig.headers[`Accept-Language`] = i18n.language;
        return requestConfig;
      }
      // Failed to acquire token, so send the abort signal right away and catch it in the app.
      const cancelConfig = {
        ...requestConfig,
        signal: abortController.signal,
      };
      abortController.abort('Aborted request due to missing authorization.');
      // eslint-disable-next-line no-console
      // console.log('Aborting request due to missing authorization header.');
      return cancelConfig;
    },
    (error) => Promise.reject(error)
  );

  const axiosHolder = {
    public: publicAxiosInstance,
    authenticated: authenticatedAxiosInstance,
  };

  const axiosRef = React.useRef(axiosHolder);

  return (
    <AxiosContext.Provider value={axiosRef.current}>
      {children}
    </AxiosContext.Provider>
  );
}

AxiosProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Custom hook to return the axios instance usable by all components of the application.
 *
 * @param requiresAuthentication true if the request will need to include the bearer token, otherwise false.
 * @returns the `axios` instance.
 */
const useAxios = (requiresAuthentication = false) => {
  const axiosHolder = useContext(AxiosContext);

  const publicAxiosInstance = useMemo(() => {
    return axiosHolder.public || axios;
  }, [axiosHolder.public]);

  const authenticatedAxiosInstance = useMemo(() => {
    return axiosHolder.authenticated || axios;
  }, [axiosHolder.authenticated]);

  if (requiresAuthentication) {
    return authenticatedAxiosInstance;
  }
  return publicAxiosInstance;
};

export { useAxios, AxiosProvider };
